import React, {useState} from "react";
import { Table, Button } from "antd";

import { useSelector } from "react-redux";
import dayjs from "dayjs";
import commaNumber from "comma-number";
import { formatToCompanyTimezone } from "../../../../lib/functions";
import { selectAuthInfo } from "../../../Auth/authSelectors";
import "../../../../components/common/bt.css";

const PRINT_COLUMN_WEIGHTS = {
  "No.": 3,
  "Voucher No": 8,
  "VCH No": 8,
  Date: 8,
  "Customer / Debtor": 13,
  Customer: 10,
  Employee: 8,
  "Created By": 8,
  "Mode of Pay": 10,
  "Sub Total": 7,
  Discount: 6,
  GST: 6,
  VAT: 6,
  "GST %": 6,
  "VAT %": 6,
  "T/Charge": 6,
  Total: 7,
  Due: 6,
  Paid: 6,
  Item: 10,
  Serials: 8,
  Warehouse: 7,
  Rate: 6,
  "Discount%": 6,
  QTY: 6,
  "Payment Cond": 9,
  Received: 7,
  "Receipt By": 8,
  "Payment Method": 10,
};

const getColWidths = (columns) => {
  const weights = columns.map((col) => PRINT_COLUMN_WEIGHTS[col.title] || 7);
  const sum = weights.reduce((acc, w) => acc + w, 0);
  return weights.map((w) => (w / sum) * 100);
};

const getColValue = (col, row) =>
  Array.isArray(col.dataIndex)
    ? col.dataIndex.reduce((acc, key) => (acc ? acc[key] : undefined), row)
    : row[col.dataIndex];

const format = commaNumber.bindWith(",", ".");

const CollectionReportTable = ({ printMode = false }) => {
    const { list, details, groupWiseItems, categoryWiseItems, receipts, loading, filters } =
        useSelector((state) => state.collectionReport);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
    });

    const handleTableChange = (pagination) => {
        setPagination(pagination);
    };

    const authInfo = useSelector(selectAuthInfo);    
    const isIndia = authInfo?.country?.toLowerCase() === "india";
    const taxLabel = isIndia ? "GST" : "VAT";

    // Summary columns for without item view
    const summaryColumns = [
        { title: "No.", render: (_, __, index) => index + 1, width: 60 },
        { title: "Voucher No", dataIndex: "sale_voucher_no" },
        {
            title: "Date", dataIndex: "created_date", render: (v) =>
                v ? formatToCompanyTimezone(v).split(" ")[0] : "-",
        },
        { title: "Customer / Debtor", dataIndex: "acc_name" },
        { title: "Employee", dataIndex: "employee_name" },
        { title: "Created By", dataIndex: "full_name" },
        {
            title: "Mode of Pay",
            dataIndex: "trans",
            render: (trans, record) =>
                `${trans?.map(t => t.to_acc_name).join(", ") || ""} ${record.is_cond === "yes" ? "By Condition" : ""}`
        },
        {
            title: "Sub Total",
            dataIndex: "sub_total",
            render: (val) => format(Number(val || 0).toFixed(2))
        },
        {
            title: "Discount",
            dataIndex: "total_discount",
            render: (val, record) =>
                record.is_cal_type === "on_total" ? format(Number(val || 0).toFixed(2)) : "---"
        },
        {
            title: taxLabel,
            dataIndex: "total_tax",
            render: (val, record) =>
                record.is_cal_type === "on_total" ? format(Number(val || 0).toFixed(2)) : "---"
        },
        {
            title: "T/Charge",
            dataIndex: "total_transport_cost",
            render: (val) => format(Number(val || 0).toFixed(2))
        },
        {
            title: "Total",
            dataIndex: "total_amount",
            render: (val) => format(Number(val || 0).toFixed(2))
        },
        {
            title: "Due",
            dataIndex: "due_amount",
            render: (val) => format(Number(val || 0).toFixed(2))
        },
        {
            title: "Paid",
            dataIndex: "paid_amount",
            render: (val) => format(Number(val || 0).toFixed(2))
        },
        !printMode && {
            title: "Actions",
            className: "no-print",
            render: (_, record) => (
                <>
                    {/* <Link to={`/sales/quick-sales-voucher/${record.sale_id}`}>
            <ReceiptOutlined style={{ color: "black", marginRight: 8 }} />
          </Link>
          <Link to={`/sales/quick-challan-voucher/${record.sale_id}`}>
            <ReceiptOutlined style={{ color: "red", marginRight: 8 }} />
          </Link> */}
                    {authInfo.role !== "user" && authInfo.acc_type !== "customer" && (
                        <>

                            {/* <Link to={`/sales/sales-update/${record.sale_id}`}>
                <EditOutlined style={{ color: "green", marginRight: 8 }} />
              </Link>
              <DeleteOutlined 
                style={{ color: "#FF0202" }} 
                onClick={() => {
                  // Add delete functionality here
               }}
              /> */}

                        </>
                    )}
                </>
            ),
        },
    ].filter(Boolean);

    // Detailed columns for with item view
    const withItemColumns = [
        { title: "No.", render: (_, __, index) => index + 1, width: 60 },
        { title: "VCH No", dataIndex: "sale_voucher_no" },
        {
            title: "Date", dataIndex: "created_date", render: (v) =>
                v ? formatToCompanyTimezone(v).split(" ")[0] : "-",
        },
        { title: "Customer", dataIndex: "acc_name" },
        { title: "Item", dataIndex: ["details", "0", "item_name"] },
        authInfo?.is_serial === "yes" && {
            title: "Serials",
            dataIndex: ["details", "0", "serials"],
            render: (serials, record) =>
                record.details[0]?.is_serial === "yes" ?
                    `(${serials?.map(s => s.serial_number).join(", ")})` : ""
        },
        authInfo?.is_warehouse === "yes" && {
            title: "Warehouse",
            dataIndex: ["details", "0", "name"]
        },
        {
            title: "Rate",
            dataIndex: ["details", "0", "item_rate"],
            render: (val) => format(Number(val || 0).toFixed(2))
        },
        {
            title: "Discount%",
            dataIndex: ["details", "0", "item_discount_per"],
            render: (val, record) => record.is_cal_type === "individual" ? `${val}%` : ""
        },
        {
            title: `${taxLabel} %`,
            dataIndex: ["details", "0", "item_tax_per"],
            render: (val, record) => record.is_cal_type === "individual" ? `${val}%` : ""
        },
        { title: "QTY", dataIndex: ["details", "0", "qty_display"] },
        {
            title: "Total",
            dataIndex: ["details", "0", "item_total"],
            render: (val) => format(Number(val || 0).toFixed(2))
        },
        {
            title: "Payment Cond",
            dataIndex: "trans",
            render: (trans, record) =>
                `${trans?.map(t => t.to_acc_name).join(", ") || ""} ${record.is_cond === "yes" ? "By Condition" : ""}`
        },
        {
            title: "Due",
            dataIndex: "due_amount",
            render: (val) => format(Number(val || 0).toFixed(2))
        },
        {
            title: "Received",
            dataIndex: "paid_amount",
            render: (val) => format(Number(val || 0).toFixed(2))
        },
    ].filter(Boolean);

    // Receipts columns
    const receiptsColumns = [
        { title: "No.", render: (_, __, index) => index + 1, width: 60 },
        { title: "VCH No", dataIndex: "rcv_code" },
        {
            title: "Date", dataIndex: "created_date", render: (v) =>
                v ? formatToCompanyTimezone(v).split(" ")[0] : "-",
        },
        { title: "Customer", dataIndex: "acc_name" },
        { title: "Receipt By", dataIndex: "full_name" },
        { title: "Payment Method", dataIndex: ["details", "0", "into_acc_name"], render: (val) => val || "-" },
        {
            title: "Received",
            dataIndex: ["details", "0", "rcv_total"],
            render: (val) => format(Number(val || 0).toFixed(2))
        },
    ];

    // Calculate totals
    const calculateTotals = () => {
        if (filters.recordType === "without item") {
            return list.reduce((acc, row) => ({
                subtotal: acc.subtotal + Number(row.sub_total || 0),
                discount: acc.discount + (row.is_cal_type === "on_total" ? Number(row.total_discount || 0) : 0),
                vat: acc.vat + (row.is_cal_type === "on_total" ? Number(row.total_tax || 0) : 0),
                service: acc.service + Number(row.total_transport_cost || 0),
                total: acc.total + Number(row.total_amount || 0),
                paid: acc.paid + Number(row.paid_amount || 0),
                due: acc.due + Number(row.due_amount || 0),
            }), {
                subtotal: 0, discount: 0, vat: 0, service: 0, total: 0, paid: 0, due: 0
            });
        } else {
            return {
                masterQty: list.reduce((sum, row) =>
                    sum + row.details.reduce((detailSum, detail) => detailSum + Number(detail.item_qty || 0), 0), 0),
                baseQty: list.reduce((sum, row) =>
                    sum + row.details.reduce((detailSum, detail) => detailSum + Number(detail.retail_qty || 0), 0), 0),
                total: list.reduce((sum, row) => sum + Number(row.total_amount || 0), 0),
                paid: list.reduce((sum, row) => sum + Number(row.paid_amount || 0), 0),
                due: list.reduce((sum, row) => sum + Number(row.due_amount || 0), 0),
            };
        }
    };

    const totals = calculateTotals();
    const receiptsTotal = receipts.reduce((sum, row) =>
        sum + Number(row.rcv_total || 0), 0);

    if (printMode) {
        const salesColumns = filters.recordType === "with item" ? withItemColumns : summaryColumns;
        const salesWidths = getColWidths(salesColumns);
        const receiptsWidths = getColWidths(receiptsColumns);

        return (
            <>
                {list.length > 0 && (
                    <table className="ledger-print-table" style={{ marginBottom: 16 }}>
                        <colgroup>
                            {salesWidths.map((w, i) => (
                                <col key={i} style={{ width: `${w}%` }} />
                            ))}
                        </colgroup>
                        <thead>
                            <tr className="ledger-print-heading">
                                {salesColumns.map((col, idx) => (
                                    <td key={idx} style={col.align === "right" ? { textAlign: "right" } : undefined}>
                                        {col.title}
                                    </td>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {list.map((row, rowIndex) => (
                                <tr key={row.sale_id || rowIndex}>
                                    {salesColumns.map((col, colIndex) => {
                                        const val = getColValue(col, row);
                                        return (
                                            <td key={colIndex} style={col.align === "right" ? { textAlign: "right" } : undefined}>
                                                {col.render ? col.render(val, row, rowIndex) : val}
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            {filters.recordType === "without item" ? (
                                <tr className="ledger-total-row">
                                    <td colSpan={7} style={{ textAlign: "right" }}>Grand Total</td>
                                    <td style={{ textAlign: "right" }}>{format(totals.subtotal.toFixed(2))}</td>
                                    <td style={{ textAlign: "right" }}>{format(totals.discount.toFixed(2))}</td>
                                    <td style={{ textAlign: "right" }}>{format(totals.vat.toFixed(2))}</td>
                                    <td style={{ textAlign: "right" }}>{format(totals.service.toFixed(2))}</td>
                                    <td style={{ textAlign: "right" }}>{format(totals.total.toFixed(2))}</td>
                                    <td style={{ textAlign: "right" }}>{format(totals.due.toFixed(2))}</td>
                                    <td style={{ textAlign: "right" }}>{format(totals.paid.toFixed(2))}</td>
                                </tr>
                            ) : (
                                <tr className="ledger-total-row">
                                    <td colSpan={salesColumns.length - 4} style={{ textAlign: "right" }}>
                                        Master U. Qty = {format(totals.masterQty.toFixed(2))}, Base U. Qty = {format(totals.baseQty.toFixed(2))}
                                    </td>
                                    <td style={{ textAlign: "right" }}>{format(totals.total.toFixed(2))}</td>
                                    <td />
                                    <td style={{ textAlign: "right" }}>{format(totals.due.toFixed(2))}</td>
                                    <td style={{ textAlign: "right" }}>{format(totals.paid.toFixed(2))}</td>
                                </tr>
                            )}
                        </tfoot>
                    </table>
                )}

                {receipts.length > 0 && (
                    <table className="ledger-print-table">
                        <colgroup>
                            {receiptsWidths.map((w, i) => (
                                <col key={i} style={{ width: `${w}%` }} />
                            ))}
                        </colgroup>
                        <thead>
                            <tr className="ledger-print-heading">
                                {receiptsColumns.map((col, idx) => (
                                    <td key={idx} style={col.align === "right" ? { textAlign: "right" } : undefined}>
                                        {col.title}
                                    </td>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {receipts.map((row, rowIndex) => (
                                <tr key={row.rcv_id || rowIndex}>
                                    {receiptsColumns.map((col, colIndex) => {
                                        const val = getColValue(col, row);
                                        return (
                                            <td key={colIndex} style={col.align === "right" ? { textAlign: "right" } : undefined}>
                                                {col.render ? col.render(val, row, rowIndex) : val}
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr className="ledger-total-row">
                                <td colSpan={6} style={{ textAlign: "right" }}>Total Receipts</td>
                                <td style={{ textAlign: "right" }}>{format(receiptsTotal.toFixed(2))}</td>
                            </tr>
                        </tfoot>
                    </table>
                )}

                {list.length > 0 && receipts.length > 0 && (
                    <div style={{ marginTop: 16, padding: 8, backgroundColor: '#f0f0f0', borderRadius: 4 }}>
                        <strong style={{ color: 'red' }}>
                            Grand Total (Sales Paid & Previous Collection): {format((totals.paid + receiptsTotal).toFixed(2))}
                        </strong>
                    </div>
                )}
            </>
        );
    }

    return (
        <>
            {/* Sales Table */}
            {list.length > 0 && (
                <Table
                    rowKey="sale_id"
                    columns={filters.recordType === "with item" ? withItemColumns : summaryColumns}
                    dataSource={list}
                    loading={loading}
                      pagination={{
                        ...pagination,
                        showSizeChanger: true,
                        pageSizeOptions: ["10", "20", "50"],
                    }}
                    onChange={handleTableChange}
                    bordered
                    scroll={printMode ? undefined : { x: 'max-content' }}
                    size={printMode ? "small" : "middle"}
                    summary={() => (
                        <Table.Summary.Row>
                            <Table.Summary.Cell index={0} colSpan={8}>
                                <strong>Grand Total</strong>
                            </Table.Summary.Cell>
                            {filters.recordType === "without item" ? (
                                <>
                                    <Table.Summary.Cell index={6}>
                                        {format(totals.subtotal.toFixed(2))}
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={7}>
                                        {format(totals.discount.toFixed(2))}
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={8}>
                                        {format(totals.vat.toFixed(2))}
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={9}>
                                        {format(totals.service.toFixed(2))}
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={10}>
                                        {format(totals.total.toFixed(2))}
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={11}>
                                        {format(totals.due.toFixed(2))}
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={12}>
                                        {format(totals.paid.toFixed(2))}
                                    </Table.Summary.Cell>
                                    {!printMode && <Table.Summary.Cell index={13}></Table.Summary.Cell>}
                                </>
                            ) : (
                                <>
                                    <Table.Summary.Cell index={9} colSpan={1} >
                                        Master U. Qty = {format(totals.masterQty.toFixed(2))},<br></br>
                                        Base U. Qty = {format(totals.baseQty.toFixed(2))}
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={10}>
                                        {format(totals.total.toFixed(2))}
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={11}>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={12}>
                                        {format(totals.due.toFixed(2))}
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={13}>
                                        {format(totals.paid.toFixed(2))}
                                    </Table.Summary.Cell>
                                </>
                            )}
                        </Table.Summary.Row>
                    )}
                />
            )}

            {/* Receipts Table */}
            {receipts.length > 0 && (
                <Table
                    rowKey="rcv_id"
                    columns={receiptsColumns}
                    dataSource={receipts}
                    loading={loading}
                    pagination={{
                        ...pagination,
                        showSizeChanger: true,
                        pageSizeOptions: ["10", "20", "50"],
                    }}
                    onChange={handleTableChange}
                    bordered
                    scroll={printMode ? undefined : { x: 'max-content' }}
                    size={printMode ? "small" : "middle"}
                    summary={() => (
                        <Table.Summary.Row>
                            <Table.Summary.Cell index={0} colSpan={6}>
                                <strong>Total Receipts</strong>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={6}>
                                {format(receiptsTotal.toFixed(2))}
                            </Table.Summary.Cell>
                        </Table.Summary.Row>
                    )}
                />
            )}

            {/* Grand Total Summary */}
            {list.length > 0 && receipts.length > 0 && (
                <div style={{ marginTop: 16, padding: 8, backgroundColor: '#f0f0f0', borderRadius: 4 }}>
                    <strong style={{ color: 'red' }}>
                        Grand Total (Sales Paid & Previous Collection): {format((totals.paid + receiptsTotal).toFixed(2))}
                    </strong>
                </div>
            )}
        </>
    );
};

export default CollectionReportTable;